// Copyright ©️ 2026 eVolve MEP, LLC

import { getObjectPropertyValueAsString } from 'components/Autodesk/extensions/propertyHelpers';
import type { SpoolsBySpoolName, SpoolsStore } from 'components/Autodesk/extensions/SpoolsExtension/createSpoolsStore';
import {
  buildSpoolObject,
  getDocumentGuids,
  getMostFrequentLevel,
  spoolObjectProperties,
} from 'components/Autodesk/extensions/SpoolsExtension/helpers';
import type { BuildingSpool } from 'components/Autodesk/extensions/SpoolsExtension/types';
import {
  findAllObjectsWithMatchingProperty,
  findAllObjectsWithProperty,
} from 'components/Autodesk/extensions/viewerHelpers';

/** Helper class to load and unload spools. */
export class SpoolLoader {
  constructor(
    private viewer: Autodesk.Viewing.GuiViewer3D,
    private readonly store: SpoolsStore,
  ) {
    this.loadSpools = this.loadSpools.bind(this);
    this.clearSpools = this.clearSpools.bind(this);
  }

  get spools() {
    return this.store.getSnapshot();
  }

  // This method loads spools that exist on the Revit model
  async loadSpools(model: Autodesk.Viewing.Model) {
    // Get what document nodes are currently visible
    const viewableGuids = getDocumentGuids(this.viewer.model.getDocumentNode());
    // Find all objects whose "Category" property contains the phrase "Revit Assemblies" (a Revit Assembly is a Spool)
    const possibleSpools = await findAllObjectsWithMatchingProperty(model, 'Category', 'Revit Assemblies', [
      'name',
      'Type Name',
      'viewable_in',
      'eV_LBS_Id',
      'eVolve_SpoolStatus',
      'eV_PackageId',
      'eV_PackageStatus',
    ]);
    const spools = possibleSpools
      .filter((o) =>
        // Now narrow down to all objects that are parent 'Revit Assemblies' objects
        o.properties.some(
          (p) =>
            p.displayCategory === '__category__' &&
            p.attributeName === 'Category' &&
            p.displayValue === 'Revit Assemblies',
        ),
      )
      .map((o): BuildingSpool => ({
        // The "Type Name" is the name of the Revit Assembly, afaict
        name: getObjectPropertyValueAsString(o, 'Type Name') ?? 'UNKNOWN',
        // Will get populated below
        objects: [],
        // Fields necessary for the Spooling page
        location: getObjectPropertyValueAsString(o, 'eV_LBS_Id'),
        spoolStatus: getObjectPropertyValueAsString(o, 'eVolve_SpoolStatus'),
        package: getObjectPropertyValueAsString(o, 'eV_PackageId'),
        packageStatus: getObjectPropertyValueAsString(o, 'eV_PackageStatus'),
      }))
      .toSorted((a, b) => a.name.localeCompare(b.name));

    // Then, find all objects with the property "Assembly Name". These might be spool objects.
    const possibleSpoolObjects = await findAllObjectsWithProperty(model, 'Assembly Name', [
      'name',
      'viewable_in',
      'eVolve_Description',
      ...spoolObjectProperties,
    ]);
    possibleSpoolObjects.forEach((obj) => {
      // Find a spool whose name matches
      const spool = spools.find((s) => obj.properties.some((p) => p.displayValue === s.name));
      // Find what document nodes `obj` is visible in
      const documentGuids = obj.properties
        .filter((p) => p.attributeName === 'viewable_in')
        .map((p) => p.displayValue as string);
      if (
        // Make sure we matched a known Spool
        spool &&
        // and make sure `obj` is viewable in the current document node tree
        documentGuids.some((d) => viewableGuids.includes(d))
      ) {
        // and if so, add our object to the spool
        // TODO: Is *every* object a Spool Item?
        spool.objects.push(buildSpoolObject(obj));
      }
    });

    // Find the most common level for each spool, assign it to "level"
    spools.forEach((spool) => {
      // oxlint-disable-next-line evolve/no-param-reassign
      spool.level = getMostFrequentLevel(spool.objects);
    });

    const finalizedSpools = spools.reduce((map, spool) => {
      map[spool.name] = spool;
      return map;
    }, {} as NonNullable<SpoolsBySpoolName>);

    this.store.setSpools(finalizedSpools);
    return finalizedSpools;
  }

  clearSpools() {
    this.store.clear();
  }
}
